import {
  Description as AwaitingDocsIcon,
  RateReview as ReviewIcon,
  ContactMail as QueryIcon,
  Send as SubmittedIcon,
  HourglassEmpty as PendingIcon,
  CheckCircle as ReadyIcon,
  ThumbUp as ApprovedIcon,
  ThumbDown as DeniedIcon,
  Block as NoAuthIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import { StatusCardProps } from '../../common/components/styled-ui-elements/StatusCard';

export type MedNecStatus =
  | 'Pending'
  | 'Under Review'
  | 'Ready for Submission'
  | 'Awaiting Physician Response'
  | 'Submitted'
  | 'Payer Approved'
  | 'Payer Denied'
  | 'No Auth Required'
  | 'Cancelled';

interface StatusConfig {
  icon: React.ComponentType<{ sx?: any }>;
  color: string;
  bgColor: string;
  displayName: string;
  description: string;
}

const STATUS_CONFIGS: Record<MedNecStatus, StatusConfig> = {
  'Pending': {
    icon: PendingIcon,
    color: '#64748b',
    bgColor: '#f1f5f9',
    displayName: 'Pending',
    description: 'Awaiting AI assessment',
  },
  'Under Review': {
    icon: ReviewIcon,
    color: '#7c3aed',
    bgColor: '#f3e8ff',
    displayName: 'Under Review',
    description: 'Nurse review in progress',
  },
  'Ready for Submission': {
    icon: ReadyIcon,
    color: '#22c55e',
    bgColor: '#f0fdf4',
    displayName: 'Ready for Submission',
    description: 'Approved, pending submission',
  },
  'Awaiting Physician Response': {
    icon: QueryIcon,
    color: '#f97316',
    bgColor: '#fff7ed',
    displayName: 'Physician Query',
    description: 'Awaiting physician response',
  },
  'Submitted': {
    icon: SubmittedIcon,
    color: '#14b8a6',
    bgColor: '#f0fdfa',
    displayName: 'Submitted',
    description: 'Sent to payer',
  },
  'Payer Approved': {
    icon: ApprovedIcon,
    color: '#16a34a',
    bgColor: '#f0fdf4',
    displayName: 'Payer Approved',
    description: 'Authorization approved',
  },
  'Payer Denied': {
    icon: DeniedIcon,
    color: '#dc2626',
    bgColor: '#fef2f2',
    displayName: 'Payer Denied',
    description: 'Authorization denied',
  },
  'No Auth Required': {
    icon: NoAuthIcon,
    color: '#6b7280',
    bgColor: '#f3f4f6',
    displayName: 'No Auth Required',
    description: 'Payer does not require prior authorization',
  },
  'Cancelled': {
    icon: CancelledIcon,
    color: '#9ca3af',
    bgColor: '#f9fafb',
    displayName: 'Cancelled',
    description: 'Case cancelled',
  },
};

/**
 * Maps backend CaseStatus short codes (e.g. "PEND") to display names.
 * This is the single source of truth for code-to-display mapping.
 */
const STATUS_CODE_MAP: Record<string, MedNecStatus> = {
  PEND: 'Pending',
  UREV: 'Under Review',
  AWPR: 'Awaiting Physician Response',
  RFSB: 'Ready for Submission',
  SUBT: 'Submitted',
  PAPP: 'Payer Approved',
  PDEN: 'Payer Denied',
  NOAR: 'No Auth Required',
  CANC: 'Cancelled',
};

/** Converts a backend status code (e.g. "PEND") to its display name. */
export function getStatusDisplayName(code: string): string {
  return STATUS_CODE_MAP[code] ?? code;
}

/** Converts a backend status code to a typed MedNecStatus, if valid. */
export function toMedNecStatus(code: string): MedNecStatus | undefined {
  return STATUS_CODE_MAP[code];
}

/** Statuses shown in the workflow dashboard, ordered for layout. */
export const WORKFLOW_STATUSES = {
  /** Linear start: Pending → Under Review */
  start: ['Pending', 'Under Review'] as MedNecStatus[],
  /** Path A (submission): Under Review → Submitted */
  pathA: ['Submitted'] as MedNecStatus[],
  /** Path B (physician query): Under Review → Awaiting Physician Response → (back to Under Review) */
  pathB: ['Awaiting Physician Response'] as MedNecStatus[],
  /** Terminal outcomes after Submitted */
  terminal: ['Payer Approved', 'Payer Denied'] as MedNecStatus[],
};

export const ACTIVE_STATUSES: MedNecStatus[] = [
  'Pending',
  'Under Review',
  'Awaiting Physician Response',
  'Submitted',
  'Payer Approved',
  'Payer Denied',
];

export interface MedNecStatusCard extends Omit<StatusCardProps, 'onClick'> {
  status: MedNecStatus;
}

export function buildStatusCards(statusCounts: Record<string, number>): MedNecStatusCard[] {
  return ACTIVE_STATUSES.map((status) => {
    const config = STATUS_CONFIGS[status];
    return {
      status,
      icon: config.icon,
      color: config.color,
      bgColor: config.bgColor,
      label: config.displayName,
      description: config.description,
      count: statusCounts[status] || 0,
    };
  });
}

export function getStatusConfig(status: MedNecStatus): StatusConfig {
  return STATUS_CONFIGS[status];
}

export function buildStatusCard(status: MedNecStatus, count: number): MedNecStatusCard {
  const config = STATUS_CONFIGS[status];
  return {
    status,
    icon: config.icon,
    color: config.color,
    bgColor: config.bgColor,
    label: config.displayName,
    description: config.description,
    count,
  };
}
